/**
 * Page: EditWebsite
 * Admin-only page to edit an existing website
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import ThumbnailUploader from '../../../components/admin/stockVideos/ThumbnailUploader';
import Footer from '../../../components/shared/Footer';
import { getWebsite, updateWebsite } from '../../../api/websites/websitesApi';
import { WEBSITE_TAG_LIST } from '../../../data/tags/tagEnums';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function EditWebsite() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    tags: [],
    websiteUrl: '',
    searchableText: '',
    thumbnailImage: null
  });

  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        loadWebsite();
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin, websiteId]);

  const loadWebsite = async () => {
    try {
      const { website } = await getWebsite(websiteId);
      setFormData({
        title: website.title || '',
        tags: website.tags || [],
        websiteUrl: website.websiteUrl || '',
        searchableText: website.searchableText || '',
        thumbnailImage: null
      });
    } catch (error) {
      console.error('Error loading website:', error);
      addToast('Failed to load website', 'error');
      navigate('/app/private/manageWebsites');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagChange = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleThumbnailChange = (thumbnailData) => {
    // Handle both file objects and null (for removal)
    if (thumbnailData === null) {
      setFormData(prev => ({ ...prev, thumbnailImage: null }));
    } else if (thumbnailData instanceof File) {
      setFormData(prev => ({ ...prev, thumbnailImage: thumbnailData }));
    } else if (thumbnailData?.isExistingThumbnail) {
      // Handle existing thumbnail case
      setFormData(prev => ({ ...prev, thumbnailImage: thumbnailData }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.websiteUrl || formData.tags.length === 0) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('websiteUrl', formData.websiteUrl);
      submitData.append('tags', formData.tags.join(','));
      submitData.append('searchableText', formData.searchableText || '');
      submitData.append('keepExistingThumbnail', formData.thumbnailImage ? 'false' : 'true');
      
      if (formData.thumbnailImage) {
        submitData.append('thumbnailImage', formData.thumbnailImage);
      }

      await updateWebsite(websiteId, submitData);
      addToast('Website updated successfully!', 'success');
      navigate('/app/private/manageWebsites');
    } catch (error) {
      console.error('Error updating website:', error);
      addToast(error.message || 'Failed to update website', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/templates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Website</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Update the website details and settings.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/manageWebsites')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          Manage Websites
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Website URL */}
          <div>
            <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Website URL *
            </label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              required
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags *
            </label>
            <div className="flex flex-wrap gap-2">
              {WEBSITE_TAG_LIST.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagChange(tag)}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    formData.tags.includes(tag)
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Searchable Text */}
          <div>
            <label htmlFor="searchableText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Terms (Optional)
            </label>
            <textarea
              id="searchableText"
              name="searchableText"
              value={formData.searchableText}
              onChange={handleInputChange}
              rows={3}
              placeholder="Add comma-separated search terms to help users find this website..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add search terms that describe the template content, style, or use cases. This will make your website easily discoverable. Tags are automatically included.
            </p>
          </div>

          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thumbnail Image
            </label>
            <ThumbnailUploader
              onThumbnailChange={handleThumbnailChange}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Upload a new thumbnail or leave empty to keep the existing one.
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Website'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/app/private/manageWebsites')}
              className="px-6 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}

